import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
import CookieConsent from "@/components/CookieConsent";
import { Toaster } from "@/components/ui/toaster";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import RouteFallback from "@/components/RouteFallback";
import { ROUTE_ALIASES } from "@/lib/routes";
// Landing is the entry/LCP route — keep it eager for fastest first paint.
import LandingPage from "@/pages/LandingPage";

// Everything else is route-split to shrink the initial bundle.
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const CookiePolicyPage = lazy(() => import("@/pages/CookiePolicyPage"));
const DataProtectionPage = lazy(() => import("@/pages/DataProtectionPage"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const RegisterWatchPage = lazy(() => import("@/pages/RegisterWatchPage"));
const BulkImportPage = lazy(() => import("@/pages/BulkImportPage"));
const ClaimPage = lazy(() => import("@/pages/ClaimPage"));
const NfcLandingPage = lazy(() => import("@/pages/NfcLandingPage"));
const ReportTheftPage = lazy(() => import("@/pages/ReportTheftPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const PaymentReturnPage = lazy(() => import("@/pages/PaymentReturnPage"));
const ReportView = lazy(() => import("@/pages/ReportView"));
const CertificateRedirect = lazy(() => import("@/pages/CertificateRedirect"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminUserDetailPage = lazy(() => import("@/pages/AdminUserDetailPage"));
const AdminWatchDetailPage = lazy(() => import("@/pages/AdminWatchDetailPage"));
const PublicProvenaPage = lazy(() => import("@/pages/PublicProvenaPage"));
const VerifyPage = lazy(() => import("@/pages/VerifyPage"));
const PassportPage = lazy(() => import("@/pages/PassportPage"));
const GalleryPage = lazy(() => import("@/pages/GalleryPage"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const StolenWatchGuidePage = lazy(() => import("@/pages/StolenWatchGuidePage"));
const StolenRegistryPage = lazy(() => import("@/pages/StolenRegistryPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const VerifyWatchPage = lazy(() => import("@/pages/VerifyWatchPage"));
const PassportInfoPage = lazy(() => import("@/pages/PassportInfoPage"));
const SignatoriesPage = lazy(() => import("@/pages/SignatoriesPage"));
const ProApplyPage = lazy(() => import("@/pages/ProApplyPage"));
const ProPortalPage = lazy(() => import("@/pages/ProPortalPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const MagicLinkPage = lazy(() => import("@/pages/MagicLinkPage"));
const AuthorizePage = lazy(() => import("@/pages/AuthorizePage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const PAGE_TRANSITION = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
};

// Une redirection qui garde la chaîne de requête et le fragment. Sans elle, une
// visite venue d'une campagne perdrait ses paramètres UTM en passant par
// l'alias, et deviendrait inattribuable — c'est-à-dire invisible là où on la
// cherche.
const AliasRedirect = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

function AppRouter() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div key={location.pathname} {...PAGE_TRANSITION}>
        <Routes location={location}>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/connexion/lien" element={<MagicLinkPage />} />
      <Route path="/autoriser" element={<AuthorizePage />} />
      <Route path="/enregistrer" element={<RegisterWatchPage />} />
      <Route path="/enregistrer/import" element={<BulkImportPage />} />
      <Route path="/revendiquer" element={<ClaimPage />} />
      <Route path="/declarer-vol" element={<ReportTheftPage />} />
      <Route path="/signaler-vol-montre" element={<StolenWatchGuidePage />} />
      <Route path="/montres-volees" element={<StolenRegistryPage />} />
      <Route path="/a-propos" element={<AboutPage />} />
      <Route path="/verifier" element={<VerifyWatchPage />} />
      <Route path="/le-passeport" element={<PassportInfoPage />} />
      {/* Adresses anglaises citées hors du site (voir `ROUTE_ALIASES`). */}
      {Object.entries(ROUTE_ALIASES).map(([alias, cible]) => (
        <Route key={alias} path={alias} element={<AliasRedirect to={cible} />} />
      ))}
      <Route path="/signataires" element={<SignatoriesPage />} />
      <Route path="/tarifs" element={<PricingPage />} />
      <Route
        path="/mon-compte"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      {/* Aliases for common private paths → same account page */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="/paiement" element={<PaymentReturnPage />} />
      <Route path="/rapport/:id" element={<ReportView />} />
      {/* Les e-mails de passeport ont longtemps pointé ici, et rien n'y
          répondait : « Consulter le document » rendait la page 404. Le gabarit
          vise désormais /passeport/<provena_id>, mais les messages déjà partis
          sont définitifs — cette route les rattrape. */}
      <Route
        path="/certificat/:id"
        element={
          <ProtectedRoute>
            <CertificateRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:userId"
        element={
          <ProtectedRoute requireAdmin>
            <AdminUserDetailPage />
          </ProtectedRoute>
        }
      />
      {/* Un utilisateur avait sa fiche, un dossier n'en avait aucune :
          le comprendre obligeait à traverser quatre onglets. */}
      <Route
        path="/admin/watches/:provenaId"
        element={
          <ProtectedRoute requireAdmin>
            <AdminWatchDetailPage />
          </ProtectedRoute>
        }
      />
      {/* Metal Passport NFC target — short, printable, burned into the chips.
          Must stay live and stable: a tap that 404s is a dead object. */}
      <Route path="/p/:provenaId" element={<NfcLandingPage />} />
      <Route path="/provena/:provenaId" element={<PublicProvenaPage />} />
      <Route path="/verify/:provenaId" element={<VerifyPage />} />
      <Route path="/passeport/:provenaId" element={<PassportPage />} />
      <Route path="/galerie" element={<GalleryPage />} />
      <Route path="/comment-ca-marche" element={<HowItWorksPage />} />
      <Route path="/professionnel" element={<ProApplyPage />} />
      <Route
        path="/pro"
        element={
          <ProtectedRoute>
            <ProPortalPage />
          </ProtectedRoute>
        }
      />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/politique-confidentialite" element={<PrivacyPolicyPage />} />
      <Route path="/politique-cookies" element={<CookiePolicyPage />} />
      <Route path="/vos-donnees" element={<DataProtectionPage />} />
      <Route path="/confidentialite" element={<PrivacyPolicyPage />} />
      <Route path="/conditions" element={<TermsPage />} />
      <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <ScrollToTop />
            <ErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
                <AppRouter />
              </Suspense>
            </ErrorBoundary>
            <CookieConsent />
            <Toaster />
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
